import { useDialogStore } from '@ariakit/react/dialog'
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { ProPhotosHeader } from '@/features/submission-pro-photos-upload/components/pro-photos-header'
import { useTranslation } from '@/translations/i18n'
import { useSubmitProPhotosExperience } from '../queries/use-submit-pro-photos-experience'
import type { RemapExperienceVetting } from '../types'
import { MINIMUM_IMAGES_REQUIRED } from './exp-pro-photos-grid'

interface ExpProPhotosHeaderProps {
	experience: RemapExperienceVetting
}

export const ExpProPhotosHeader = ({ experience }: ExpProPhotosHeaderProps) => {
	const { t } = useTranslation()
	const submitModalStore = useDialogStore()
	const submitQuery = useSubmitProPhotosExperience(
		{ experienceId: experience.id },

		{
			onSuccess: () => {
				toast.success(
					t('vetting:reordering.submit_photo_modal.success_toast')
				)
				submitModalStore.toggle()
			}
		}
	)

	const handleSubmit = () => {
		submitQuery.mutate({ isWaitlisted: false, isHostApprovalNeeded: false })
	}

	const { hasErrors, errorMessage } = useMemo(() => {
		const photoCount = experience?.gallery.images.length ?? 0
		const hasItinerariesPhotoMissing = experience?.itineraries.some(
			(itinerary) => !itinerary.coverImage?.url
		)
		const hasGalleryCoverPhoto = experience?.gallery.images.some(
			(image) => image.isCover
		)

		const photoCountError = photoCount < MINIMUM_IMAGES_REQUIRED
		const coverPhotoError = !hasGalleryCoverPhoto

		const hasErrors =
			photoCountError || hasItinerariesPhotoMissing || coverPhotoError

		if (hasErrors) {
			const errorParts: string[] = []

			if (photoCountError) {
				errorParts.push(
					t('vetting:reordering.gallery_contains_at_least_x_photos', {
						count: MINIMUM_IMAGES_REQUIRED
					})
				)
			}

			if (coverPhotoError) {
				errorParts.push(t('vetting:reordering.cover_selected'))
			}

			if (hasItinerariesPhotoMissing) {
				errorParts.push(
					t('vetting:reordering.activities_photo_selected')
				)
			}

			let errorMessage = t('vetting:reordering.please_ensure_that')

			if (errorParts.length === 1) {
				errorMessage += errorParts[0]
			} else if (errorParts.length === 2) {
				errorMessage += `${errorParts[0]} ${t('common:and')} ${errorParts[1]}`
			} else {
				const lastPart = errorParts.pop()
				errorMessage += `${errorParts.join(', ')} ${t('common:and')} ${lastPart}`
			}

			errorMessage += '.'

			return { hasErrors, errorMessage }
		}

		return { hasErrors, errorMessage: '' }
	}, [experience, t])

	const isFieldsChanged = useMemo(() => {
		const isNewPhotoAdded = experience?.gallery.images.some(
			(image) => image.isPending
		)
		return experience.isUpdateListingNeeded || isNewPhotoAdded
	}, [experience])

	return (
		<ProPhotosHeader
			description={t('vetting:reordering.experience.description')}
			errorDescription={errorMessage}
			hasErrors={hasErrors}
			isFieldsChanged={isFieldsChanged}
			isPending={submitQuery.isPending}
			onSubmit={handleSubmit}
			submitModalStore={submitModalStore}
			title={t('vetting:reordering.experience.title')}
			updatedAt={experience.latestUpdate?.updatedAt}
			updatedBy={experience.latestUpdate?.userId}
		/>
	)
}
